import { Injectable, NgZone } from '@angular/core';

/**
 * Tasto destro su una riga di tabella = apre il menu azioni (icona ☰) di quella riga
 * sul puntatore, come un menu contestuale. Vale per TUTTE le tabelle del portale con il
 * dropdown ngx-bootstrap per riga: app-kcs-tabella con "Menu a tendina" e le tabelle
 * scritte a mano (cantieri, gare, scadenziario, RFI...), presenti e future.
 *
 * Si clicca il `dropdownToggle` della riga, quindi voci e azioni sono esattamente quelle
 * del menu. Le righe senza menu (azioni "Icone in linea", nessuna azione), i link e i campi
 * di input tengono il menu del browser.
 *
 * ngx-bootstrap continua a posizionare il contenitore del menu sull'icona (transform,
 * ricalcolato a ogni scroll/render): qui si sposta solo il `ul` interno con la proprietà CSS
 * `translate`, che si somma al transform del contenitore e non viene sovrascritta.
 *
 * Doppio click su una riga = esegue l'azione principale della riga: il dettaglio, se manca la
 * modifica (per navigare la tabella con il doppio click). La voce si cerca tra le azioni in linea della riga oppure nel
 * suo menu, aperto senza mostrarlo: per mdfu_function (attributo data-kcs-fn messo da
 * app-kcs-azioni), altrimenti per testo/title ("Dettaglio", "Visualizza") nelle tabelle scritte a mano.
 *
 * Va inizializzato una sola volta (AppComponent.ngOnInit → `init()`).
 */
@Injectable({ providedIn: 'root' })
export class TableRowContextMenuService {
	/** sul tasto destro di questi elementi resta il menu del browser (es. "Apri in una nuova scheda", incolla) */
	private static readonly NATIVE = 'a[href], input, select, textarea, [contenteditable]';
	/** classe su <html> che nasconde il menu finché non è spostato sul puntatore (niente sfarfallio sull'icona) */
	private static readonly PENDING = 'kcs-ctxmenu-pending';
	/** classe sulla riga del menu aperto: resta evidenziata anche quando il mouse passa sul menu */
	private static readonly ROW = 'kcs-ctxmenu-row';
	/** frame di attesa massimi per l'apertura del menu (ngx-bootstrap lo crea in modo asincrono) */
	private static readonly MAX_FRAMES = 10;
	/** sul doppio click di questi elementi non si apre il dettaglio (restano interattivi) */
	private static readonly INTERACTIVE = 'a, button, input, select, textarea, label, [contenteditable], .dropdown, .p-checkbox, .p-select, .p-toggleswitch';
	/** azione principale, in ordine di preferenza: il dettaglio, se manca la modifica. Nome intero o nome + "_..."
	 *  (view_sal, upd_reparto), mai un prefisso libero: "mod" non deve prendere un futuro "modulo_x" */
	private static readonly DETAIL_FUNCTIONS = ['dettaglio', 'info', 'visualizza', 'view', 'modifica', 'mod', 'edit', 'update', 'upd'];
	/** mai sul doppio click: PDF, segreti (view_secret) e azioni distruttive, anche se il nome inizia come un dettaglio */
	private static readonly NOT_DETAIL = /pdf|secret|segret|delete|elimina/i;

	/** menu spostati sul puntatore: al prossimo click sull'icona tornano al loro posto */
	private moved: HTMLElement[] = [];
	private activeRow: HTMLElement | null = null;

	constructor(private zone: NgZone) {}

	init(): void {
		this.zone.runOutsideAngular(() => {
			document.addEventListener('contextmenu', this.onContextMenu);
			document.addEventListener('click', this.onClickCapture, true);
			document.addEventListener('dblclick', this.onDblClick);
		});
	}

	private onDblClick = (e: MouseEvent): void => {
		const target = e.target as HTMLElement;
		if (!target || target.closest(TableRowContextMenuService.INTERACTIVE)) return;
		const row = target.closest('tr');
		if (!row || row.closest('thead, tfoot')) return;

		// azioni in linea nella riga (azioniLayout "inline")
		const inline = this.findDetailItem(this.ownItems(row, row));
		if (inline) {
			window.getSelection()?.removeAllRanges(); // il doppio click seleziona una parola
			inline.click();
			return;
		}

		// azioni nel menu ☰: aperto nascosto, si clicca la voce di dettaglio e si richiude
		const toggle = this.findToggle(row);
		if (!toggle) return;
		window.getSelection()?.removeAllRanges();
		document.body.click();
		document.documentElement.classList.add(TableRowContextMenuService.PENDING);
		toggle.click();
		this.clickDetailWhenShown(toggle, 0);
	};

	private clickDetailWhenShown(toggle: HTMLElement, frame: number): void {
		requestAnimationFrame(() => {
			const menu = this.findOpenMenu(toggle);
			if (!menu && frame < TableRowContextMenuService.MAX_FRAMES) {
				this.clickDetailWhenShown(toggle, frame + 1);
				return;
			}
			const item = menu ? this.findDetailItem(Array.from(menu.querySelectorAll<HTMLElement>('a.dropdown-item'))) : null;
			// cliccare la voce chiude il menu (autoClose di ngx-bootstrap); senza dettaglio lo chiude il click sul body
			if (item) item.click();
			else document.body.click();
			document.documentElement.classList.remove(TableRowContextMenuService.PENDING);
		});
	}

	/** Voci di menu della riga, escluse quelle di eventuali tabelle annidate nelle celle */
	private ownItems(root: HTMLElement, row: HTMLElement): HTMLElement[] {
		// voci link (app-kcs-azioni, menu a mano) e bottoni icona in linea con aria-label (es. "Modifica")
		return Array.from(root.querySelectorAll<HTMLElement>('a.dropdown-item, button[aria-label]')).filter((a) => a.closest('tr') === row);
	}

	/** Azione principale (dettaglio, altrimenti modifica): per mdfu_function, poi per testo/title; mai PDF o segreti */
	private findDetailItem(items: HTMLElement[]): HTMLElement | null {
		for (const fn of TableRowContextMenuService.DETAIL_FUNCTIONS) {
			const hit = items.find((a) => {
				const f = (a.dataset['kcsFn'] || '').toLowerCase();
				return (f === fn || f.startsWith(fn + '_')) && !TableRowContextMenuService.NOT_DETAIL.test(f);
			});
			if (hit) return hit;
		}
		const text = (a: HTMLElement) => (a.title || a.getAttribute('aria-label') || a.textContent || '').trim();
		return (
			items.find((a) => /^(dettaglio|visualizza)/i.test(text(a)) && !TableRowContextMenuService.NOT_DETAIL.test(text(a))) ??
			items.find((a) => /^modifica/i.test(text(a))) ??
			null
		);
	}

	private onContextMenu = (e: MouseEvent): void => {
		const target = e.target as HTMLElement;
		if (!target || target.closest(TableRowContextMenuService.NATIVE)) return;
		const row = target.closest('tr');
		const toggle = row ? this.findToggle(row) : null;
		if (!row || !toggle) return;
		e.preventDefault();

		// chiude i menu già aperti (ngx-bootstrap li chiude al click sinistro sul document), poi apre quello della riga
		document.body.click();
		this.setActiveRow(row);
		document.documentElement.classList.add(TableRowContextMenuService.PENDING);
		toggle.click();
		this.placeWhenShown(toggle, row, e.clientX, e.clientY, 0);
	};

	private setActiveRow(row: HTMLElement | null): void {
		this.activeRow?.classList.remove(TableRowContextMenuService.ROW);
		row?.classList.add(TableRowContextMenuService.ROW);
		this.activeRow = row;
	}

	/** Toglie l'evidenziazione della riga quando il menu si chiude (click fuori, voce scelta, Esc) */
	private watchClose(menu: HTMLElement, row: HTMLElement): void {
		requestAnimationFrame(() => {
			if (this.activeRow !== row) return; // nel frattempo è stato aperto il menu di un'altra riga
			if (menu.isConnected && menu.getClientRects().length) {
				this.watchClose(menu, row);
				return;
			}
			this.setActiveRow(null);
		});
	}

	/** Icona del menu della riga, escluse quelle di eventuali tabelle annidate nelle celle.
	 *  Prima il menu azioni ☰ (`.kcs-togglelink`): altri dropdown della riga (es. le stampe PDF dello
	 *  scadenziario, `.kcs-togglebutton`) vengono prima nel DOM ma non sono il menu azioni. */
	private findToggle(row: HTMLTableRowElement): HTMLElement | null {
		const toggles = Array.from(row.querySelectorAll<HTMLElement>('[dropdowntoggle], [bsdropdowntoggle]')).filter((t) => t.closest('tr') === row);
		return toggles.find((t) => t.classList.contains('kcs-togglelink')) ?? toggles[0] ?? null;
	}

	/** Menu aperto dal toggle: inline nel dropdown, oppure in bs-dropdown-container su body (container="body") */
	private findOpenMenu(toggle: HTMLElement): HTMLElement | null {
		const inline = toggle.closest('[dropdown], [bsdropdown]')?.querySelector<HTMLElement>('.dropdown-menu');
		// inline il ul resta nel DOM anche da chiuso (display: none): conta solo quando è visibile
		if (inline) return inline.getClientRects().length ? inline : null;
		const containers = document.querySelectorAll<HTMLElement>('bs-dropdown-container');
		const container = containers[containers.length - 1];
		// il contenitore ha il transform solo dopo il primo posizionamento di ngx-bootstrap
		return container?.style.transform ? container.querySelector<HTMLElement>('.dropdown-menu') : null;
	}

	private placeWhenShown(toggle: HTMLElement, row: HTMLElement, x: number, y: number, frame: number): void {
		requestAnimationFrame(() => {
			const menu = this.findOpenMenu(toggle);
			if (!menu && frame < TableRowContextMenuService.MAX_FRAMES) {
				this.placeWhenShown(toggle, row, x, y, frame + 1);
				return;
			}
			if (menu) {
				this.moveTo(menu, x, y);
				this.watchClose(menu, row);
			} else if (this.activeRow === row) {
				this.setActiveRow(null);
			}
			document.documentElement.classList.remove(TableRowContextMenuService.PENDING);
		});
	}

	/** Sposta il menu sul puntatore tenendolo dentro la finestra */
	private moveTo(menu: HTMLElement, x: number, y: number): void {
		const r = menu.getBoundingClientRect();
		const left = Math.max(4, Math.min(x, window.innerWidth - r.width - 4));
		const top = y + r.height > window.innerHeight - 4 ? Math.max(4, y - r.height) : y;
		menu.style.setProperty('translate', `${left - r.left}px ${top - r.top}px`);
		this.moved.push(menu);
	}

	private onClickCapture = (e: MouseEvent): void => {
		if (!this.moved.length) return;
		if (!(e.target as HTMLElement)?.closest?.('[dropdowntoggle], [bsdropdowntoggle]')) return;
		// apertura normale dall'icona: il menu torna sotto l'icona
		this.moved.forEach((m) => m.style.removeProperty('translate'));
		this.moved = [];
	};
}
